
import { useEffect, useRef, useState } from 'react';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import ReadyToStartSection from '../../../components/feature/ReadyToStartSection';
import Button from '../../../components/base/Button';

export default function Enroll() {
  const [visibleSteps, setVisibleSteps] = useState<number[]>([]);
  const stepsRef = useRef<(HTMLDivElement | null)[]>([]);

  const enrollmentSteps = [
    {
      step: 1,
      title: 'Initial Inquiry',
      description: 'Contact us to learn about availability and schedule a tour of our facilities.',
      details: [
        'Call us at (410) 676-9002 or fill out our contact form',
        'Discuss your child\'s needs and our program options',
        'Schedule a convenient time for your family tour',
        'Receive information packet about our programs'
      ],
      icon: 'ri-phone-line',
      color: '#A8D8F0'
    },
    {
      step: 2,
      title: 'Tour & Meet',
      description: 'Visit our center to see our classrooms, meet our staff, and experience our environment.',
      details: [
        'Guided tour of age-appropriate classrooms',
        'Meet teachers and administrative staff',
        'Observe children engaged in daily activities',
        'Ask questions about our curriculum and policies'
      ],
      icon: 'ri-building-line',
      color: '#BFE6CC'
    },
    {
      step: 3,
      title: 'Application & Enrollment',
      description: 'Complete the enrollment process and prepare for your child\'s first day.',
      details: [
        'Submit completed enrollment application',
        'Provide required medical and emergency forms',
        'Pay registration fee and first month\'s tuition',
        'Schedule orientation and transition visits'
      ],
      icon: 'ri-file-text-line',
      color: '#A8D8F0'
    }
  ];

  const requirements = [
    {
      title: 'Age Requirements',
      items: [
        'Infants: 6 weeks to 12 months',
        'Toddlers: 12 months to 2 years',
        'Preschool: 2 to 4 years',
        'Pre-K: 4 to 5 years'
      ]
    },
    {
      title: 'Required Documents',
      items: [
        'Completed enrollment application',
        'Child\'s birth certificate',
        'Current immunization records',
        'Medical examination form',
        'Emergency contact information'
      ]
    },
    {
      title: 'Health & Safety',
      items: [
        'Up-to-date vaccinations required',
        'Annual health checkups',
        'Allergy and dietary restrictions noted',
        'Emergency medical authorization'
      ]
    }
  ];

  useEffect(() => {
    const observers = stepsRef.current.map((step, index) => {
      if (!step) return null;
      
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setVisibleSteps(prev => [...prev, index]);
            }, index * 120);
          }
        },
        { threshold: 0.2 }
      );
      
      observer.observe(step);
      return observer;
    });

    return () => {
      observers.forEach(observer => observer?.disconnect());
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#FFF7E8]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6" style={{ fontFamily: 'Poppins, sans-serif' }}>
            How to Enroll
          </h1>
          <p className="text-xl text-[#333333]/80 max-w-3xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
            Starting your child's journey with us is simple. Follow these three easy steps to join our learning community.
          </p>
        </div>
      </section>

      {/* Enrollment Steps */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="space-y-12">
            {enrollmentSteps.map((step, index) => (
              <div
                key={step.step}
                ref={el => stepsRef.current[index] = el}
                className={`transition-all duration-400 ${
                  visibleSteps.includes(index) 
                    ? index % 2 === 0 
                      ? 'translate-x-0 opacity-100' 
                      : 'translate-x-0 opacity-100'
                    : index % 2 === 0 
                      ? '-translate-x-8 opacity-0' 
                      : 'translate-x-8 opacity-0'
                }`}
              >
                <div className={`flex flex-col ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'} items-center gap-8`}>
                  {/* Step Content */}
                  <div className="flex-1 bg-white rounded-2xl shadow-lg p-8">
                    <div className="flex items-center mb-6">
                      <div 
                        className="w-16 h-16 flex items-center justify-center rounded-full mr-6"
                        style={{ backgroundColor: `${step.color}20` }}
                      >
                        <i className={`${step.icon} text-2xl`} style={{ color: step.color }}></i>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-[#333333]/60 mb-1">Step {step.step}</div>
                        <h3 className="text-2xl font-bold text-[#333333]" style={{ fontFamily: 'Poppins, sans-serif' }}>
                          {step.title}
                        </h3>
                      </div>
                    </div>
                    
                    <p className="text-[#333333]/80 mb-6 text-lg" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                      {step.description}
                    </p>
                    
                    <ul className="space-y-3">
                      {step.details.map((detail, detailIndex) => (
                        <li key={detailIndex} className="flex items-start">
                          <i className="ri-check-line text-[#BFE6CC] text-lg mr-3 mt-0.5 flex-shrink-0"></i>
                          <span className="text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                            {detail}
                          </span>
                        </li>
                      ))}
                    </ul>
                    
                    {step.step === 3 && (
                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <a 
                          href="/admissions" 
                          className="inline-flex items-center px-6 py-3 bg-[#A8D8F0] text-white font-semibold rounded-lg hover:bg-[#A8D8F0]/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
                          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                        >
                          <i className="ri-download-line mr-2"></i>
                          Download Forms & Documents
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* Step Number */}
                  <div className="flex-shrink-0">
                    <div 
                      className="w-24 h-24 flex items-center justify-center rounded-full text-4xl font-bold text-white"
                      style={{ backgroundColor: step.color }}
                    >
                      {step.step}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
              Enrollment Requirements
            </h2>
            <p className="text-lg text-[#333333]/80 max-w-2xl mx-auto" style={{ fontFamily: 'Open Sans, sans-serif' }}>
              Everything you need to know before enrolling your child
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {requirements.map((req, index) => (
              <div key={index} className="bg-[#FFF7E8] rounded-xl p-6">
                <h3 className="text-xl font-semibold text-[#333333] mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {req.title}
                </h3>
                <ul className="space-y-2">
                  {req.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-start">
                      <i className="ri-check-line text-[#A8D8F0] text-lg mr-3 mt-0.5 flex-shrink-0"></i>
                      <span className="text-[#333333]/80" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ReadyToStartSection />
      <Footer />
    </div>
  );
}
